
import React, { useState, useMemo } from 'react';
import ServicesComponent from '../../components/Services/ServicesComponent';
import { Language, User, ServiceDefinition } from '../../types';
import { TRANSLATIONS } from '../../constants';
import { ServicesService, UsageService } from '../../api'; // Import UsageService
import { useDataTemplate } from '../../hooks/useDataTemplate';

interface ServicesTemplateProps {
  language: Language;
  user: User;
  services: ServiceDefinition[];
  onSelectService: (serviceName: string) => void;
  onAutomateService: (service: ServiceDefinition) => void;
  onOpenPlans: () => void;
}

const ServicesTemplate: React.FC<ServicesTemplateProps> = (props) => {
  const t = TRANSLATIONS[props.language];
  const { data: services, isLoading } = useDataTemplate<ServiceDefinition[]>(ServicesService.getAll);

  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedService, setSelectedService] = useState<ServiceDefinition | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const categories = [
    { id: 'all', label: t.catAll, icon: 'fa-layer-group' },
    { id: 'media', label: t.catMedia, icon: 'fa-film' },
    { id: 'content', label: t.catContent, icon: 'fa-pen-nib' },
    { id: 'business', label: t.catBusiness, icon: 'fa-briefcase' },
    { id: 'creative', label: t.catCreative, icon: 'fa-palette' },
    { id: 'dev', label: t.catDev, icon: 'fa-code' },
  ];

  const filteredServices = useMemo(() => {
    if (!services) return [];
    return services.filter(s => {
      if (s.hidden) return false; 
      const matchesCategory = activeCategory === 'all' || s.category === activeCategory;
      const matchesSearch = s.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            s.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery, services]);

  const isPro = props.user.plan === 'pro' || props.user.plan === 'enterprise';

  const handleRunService = async (service: ServiceDefinition) => {
    // 1. Strict Permission Check
    const permission = await UsageService.checkPermission('run_service', service);
    
    if (!permission.allowed) {
      // If blocked, prompt upgrade
      alert(permission.reason); // Or use a proper toast if available
      props.onOpenPlans();
    } else {
      // 2. Track Usage
      await UsageService.trackEvent('service_run', service.id);
      
      props.onSelectService(service.title);
      setSelectedService(null);
    }
  };

  const handleAutomateService = async (service: ServiceDefinition) => {
    // 1. Strict Permission Check for Automation
    const permission = await UsageService.checkPermission('create_flow');
    
    if (!permission.allowed) {
      props.onOpenPlans();
    } else {
      props.onAutomateService(service);
      setSelectedService(null);
    }
  };

  if (isLoading) return <div className="flex h-full items-center justify-center"><i className="fas fa-circle-notch fa-spin text-emerald-500 text-2xl"></i></div>;

  return (
    <ServicesComponent
      language={props.language}
      categories={categories}
      activeCategory={activeCategory}
      searchQuery={searchQuery}
      filteredServices={filteredServices}
      selectedService={selectedService}
      isSidebarOpen={isSidebarOpen}
      isPro={isPro}
      onSetActiveCategory={setActiveCategory}
      onSetSearchQuery={setSearchQuery}
      onSelectService={setSelectedService}
      onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
      onRunService={handleRunService}
      onAutomateService={handleAutomateService}
      onOpenPlans={props.onOpenPlans}
    />
  );
};

export default ServicesTemplate;
